const MAX_QUOTE_LENGTH = 60

const REPLY_PREFIX_RE = /^([A-Za-z0-9_-]+) said "([^"]*)";\s*([\s\S]*)$/
const REPLY_PREFIX_QUOT_RE =
    /^([A-Za-z0-9_-]+) said &quot;([\s\S]*?)&quot;;\s*([\s\S]*)$/

const stripHtml = (text: string): string => {
    if (typeof document === 'undefined') {
        return text.replace(/<[^>]*>/g, '')
    }
    const el = document.createElement('div')
    el.innerHTML = text
    return el.innerText || el.textContent || ''
}

export type QuoteTextOptions = {
    // Resolves a lounge `{{model|<id>}}` token to a username/slug. When
    // provided, the model preview becomes part of the quote (e.g. so users can
    // reply to a lounge image preview). When omitted, the token is stripped
    // along with other template tokens.
    resolveModelName?: (id: number) => string | undefined
}

export const quoteText = (
    text: string,
    options: QuoteTextOptions = {},
): string => {
    if (!text) return ''
    let s = stripHtml(text)
    s = s.replace(/\s+/g, ' ').trim()
    s = s.replace(/^[A-Za-z0-9_-]+ said "[^"]*";\s*/, '')
    s = s.replace(/"/g, '')
    // Resolve lounge model previews to their username so they're quotable.
    if (options.resolveModelName) {
        const resolveModelName = options.resolveModelName
        s = s.replace(/\{\{model\|(\d+)\}\}/g, (_match, idStr: string) => {
            return resolveModelName(Number(idStr)) ?? ''
        })
    }
    // Strip remaining template tokens (e.g. "{{whiteboard}}",
    // "{{MFCShareAlbums|...}}") and emote shortcuts (e.g. ":1:") so the quote
    // shows actual text only.
    s = s.replace(/\{\{[^}]+\}\}/g, '')
    s = s.replace(/:\d+:/g, '')
    s = s.replace(/\s+/g, ' ').trim()
    if (!/\w/.test(s)) return ''
    if (s.length > MAX_QUOTE_LENGTH) {
        s = s.substring(0, MAX_QUOTE_LENGTH) + '…'
    }
    return s
}

// Build a stable key used to match a reply back to its source message. Replies
// don't carry the source message's id, so we match on the (case-insensitive)
// author username plus the normalized quote text — the same `quoteText()`
// output stored in `replyTo.quoted`. Both the source rows and the reply lookup
// derive their key from this helper so the formats always agree. The `|`
// delimiter is unambiguous because usernames are restricted to `[A-Za-z0-9_-]`.
export const getReplySourceKey = (username = '', quote = ''): string =>
    `${username.toLowerCase()}|${quote}`

export type ParsedReply = {
    username: string
    quoted: string
    reply: string
}

export const parseReply = (text: string): ParsedReply | null => {
    if (!text || text.indexOf('said') === -1) return null
    const trimmed = text.replace(/^\s+/, '').replace(/\s+$/, '')
    const m =
        trimmed.match(REPLY_PREFIX_RE) ?? trimmed.match(REPLY_PREFIX_QUOT_RE)
    if (!m) return null
    const [, username, quoted, reply] = m
    if (!reply || reply.trim().length === 0) return null
    return { username, quoted, reply }
}

export const formatReplyWireMessage = (
    username: string,
    quoted: string,
    reply: string,
): string => `${username} said "${quoted}"; ${reply}`

// Detect a reply-formatted wire message and split it into a clean reply text +
// a `replyTo` block matching `Message.replyTo`. Returns the message unchanged
// when not a reply or when the message kind shouldn't be parsed (system,
// news, etc.).
//
// `resolveUserId` and `resolveModelName` are injected so this helper stays
// free of state-layer imports — callers pass their own resolvers (e.g.
// `slugToID`, `USERSSTATE.users[id]?.name`).
export const applyReplyMarkup = <
    M extends {
        system?: boolean
        html?: boolean
        news?: boolean
        chatCard?: boolean
        tip?: boolean
        welcome?: boolean
        text?: string
        replyTo?: { username: string; quoted: string; userId?: number }
    },
>(
    message: M,
    options: {
        resolveUserId?: (username: string) => number | undefined
        resolveModelName?: (id: number) => string | undefined
    } = {},
): M => {
    if (
        !message ||
        message.system ||
        message.news ||
        message.chatCard ||
        message.tip ||
        message.welcome ||
        message.replyTo ||
        typeof message.text !== 'string'
    ) {
        return message
    }
    const parsed = parseReply(message.text)
    if (!parsed) return message
    const userId = options.resolveUserId?.(parsed.username)
    return {
        ...message,
        text: parsed.reply,
        replyTo: {
            username: parsed.username,
            quoted: quoteText(parsed.quoted, {
                resolveModelName: options.resolveModelName,
            }),
            ...(userId ? { userId } : {}),
        },
    }
}
